import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Check, X, Ticket } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";

export default function Rsvp() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api
      .get(`/public/registrations/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(errText(e)));
  }, [token]);

  const answer = async (choice) => {
    setBusy(choice);
    try {
      const { data: res } = await api.post(`/public/registrations/${token}/rsvp`, { answer: choice });
      setData({ ...data, registration: { ...data.registration, rsvp: choice }, pass_available: res.pass_available });
      toast.success(choice === "yes" ? "Thanks — we've saved your Yes" : "Thanks for letting us know");
      if (res.pass_emailed) toast.info("Your entry pass is on its way by email");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  if (error)
    return (
      <Shell bare>
        <h1 className="font-display text-3xl font-black uppercase" data-testid="rsvp-error">
          {error}
        </h1>
      </Shell>
    );
  if (!data)
    return (
      <Shell bare>
        <p className="text-sm text-slate-500">Loading invitation…</p>
      </Shell>
    );

  const { registration: r, event } = data;
  const page = data.rsvp_page || {};
  const fill = (text) =>
    (text || "")
      .replaceAll("{first_name}", r.first_name || "")
      .replaceAll("{last_name}", r.last_name || "")
      .replaceAll("{salutation}", r.salutation || "")
      .replaceAll("{salutation_name}", r.salutation ? `${r.salutation} ${r.last_name}` : r.first_name)
      .replaceAll("{event_name}", event.name || "")
      .replaceAll("{date}", event.date || "")
      .replaceAll("{time}", event.start_time || "")
      .replaceAll("{venue}", event.venue || "")
      .replaceAll("{host_org}", event.host_org || "")
      .replaceAll("{reference}", r.registration_code || "")
      .replaceAll("{email}", r.email || "");

  return (
    <Shell bare>
      <div className="max-w-2xl rise" data-testid="rsvp-page">
        <p className="overline">Invitation</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
          {event.name}
        </h1>

        <div className="card p-6 mt-8">
          <p className="text-sm text-slate-300" data-testid="rsvp-intro">
            {fill(page.intro)}
          </p>
          <div className="border-t border-[#252f3e] mt-5 pt-5 space-y-2.5 text-sm text-slate-300">
            <p className="flex items-center gap-2">
              <CalendarDays size={14} className="text-[#00e599]" /> {event.date} · {event.start_time}
              {event.end_time ? `–${event.end_time}` : ""}
            </p>
            <p className="flex items-center gap-2">
              <MapPin size={14} className="text-[#00e599]" /> {event.venue}
            </p>
          </div>

          {r.rsvp ? (
            <div className="mt-6" data-testid="rsvp-answered">
              <p className="font-mono2 text-xs px-3 py-1.5 rounded-full border w-fit border-[#00e599]/40 text-[#00e599]">
                {r.rsvp === "yes" ? fill(page.answered_yes) : fill(page.answered_no)}
              </p>
              {r.rsvp === "yes" && fill(page.thanks_yes).trim() && (
                <p className="text-sm text-slate-300 mt-4" data-testid="rsvp-thanks">
                  {fill(page.thanks_yes)}
                </p>
              )}
              <div className="flex flex-wrap gap-3 mt-5">
                {data.pass_available && page.show_pass_link !== false && (
                  <Link to={`/ticket/${token}`} className="btn-primary" data-testid="rsvp-view-pass-btn">
                    <Ticket size={15} /> {fill(page.pass_link_label)}
                  </Link>
                )}
              </div>
              {r.rsvp === "yes" && !data.pass_available && (
                <p className="text-xs text-slate-400 mt-4" data-testid="rsvp-pass-pending">
                  {fill(page.pass_pending)}
                </p>
              )}
            </div>
          ) : (
            <div className="mt-6">
              <p className="label">{fill(page.question)}</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => answer("yes")}
                  disabled={!!busy}
                  className="btn-primary"
                  data-testid="rsvp-yes-btn"
                >
                  <Check size={16} /> {busy === "yes" ? "Saving…" : fill(page.yes_label)}
                </button>
                <button
                  onClick={() => answer("no")}
                  disabled={!!busy}
                  className="btn-secondary"
                  data-testid="rsvp-no-btn"
                >
                  <X size={16} /> {busy === "no" ? "Saving…" : fill(page.no_label)}
                </button>
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-4" data-testid="rsvp-footer">
          {fill(page.footer)}
        </p>
      </div>
    </Shell>
  );
}
